import * as React from "react";
import { dropLast } from "ramda";

type HelperArrayType = {
  index: number;
  value: string;
  parsedHtml: Record<string, string>;
  orgHtml: string;
};

type HelperResponse = Array<string | HelperArrayType> | string;

const getInfoFromHtmlTag = (
  html: string,
  nodesLength: number,
): string | HelperArrayType => {
  const startTag = html.match(/<[^>]*>/);
  const endTag = html.match(/<\/[^>]*>/);

  if (!startTag || !endTag) {
    return html;
  }

  const startTagProps = startTag[0].match(/ [^"]*="[^"]*"/g);

  return {
    index: nodesLength + 1,
    orgHtml: html,
    value: dropLast(
      endTag[0].length,
      html.slice((startTag?.index || 0) + startTag[0].length),
    ),
    parsedHtml: startTagProps
      ? startTagProps.reduce((acc, next) => {
          const [name, value] = next.slice(1).split("=");

          return { ...acc, [name]: value.replace(/"/g, "") };
        }, {})
      : "",
  };
};

const matchString = (text: string): RegExpMatchArray | null =>
  text.match(/(<[^>]*>)[^>]*(<\/[^>]*>)/);

const createNodes = (text: string, cutTheP: boolean): HelperResponse => {
  const nodes: Array<string | HelperArrayType> = [];
  if (cutTheP) {
    text = text.slice(3, -4);
  }
  const firstMatch = matchString(text);

  if (!firstMatch || typeof firstMatch?.index !== "number") {
    return text;
  }

  const getNodesLength = (): number =>
    nodes.filter((x) => typeof x !== "string").length - 1;

  nodes.push(text.slice(0, firstMatch.index));
  nodes.push(getInfoFromHtmlTag(firstMatch[0], getNodesLength()));

  let newText = text.slice(firstMatch.index + firstMatch[0].length);
  let loopEnabled = Boolean(matchString(newText));

  if (loopEnabled) {
    while (loopEnabled) {
      const match = matchString(newText);

      if (!match) {
        nodes.push(newText);
        loopEnabled = false;
        break;
      }

      nodes.push(newText.slice(0, match.index));
      nodes.push(getInfoFromHtmlTag(match[0], getNodesLength()));

      newText = newText.slice((match?.index || 0) + match[0].length);
    }
  } else {
    nodes.push(newText);
  }

  return nodes;
};

/*
Example:

<HtmlParser
  text="Github <a href="https://github.com/RadovanPelka">RadoG</a>"
  values={(html) => (
    <Tooltip label={html.parsedHtml.href}>
      <b>{html.value}</b>
    </Tooltip>
  )}
/>

*/

const HtmlParser: React.FC<{
  text: string;
  children?: never;
  wrapToSpan?: boolean;
  values?: (html: HelperArrayType) => React.ReactNode;
  cutOutPElement?: boolean;
}> = ({
  text,
  wrapToSpan,
  values = (html) => html.value,
  cutOutPElement = true,
}) => {
  const [toRender, setRender] = React.useState<HelperResponse>(() =>
    createNodes(text, cutOutPElement),
  );

  React.useEffect(() => {
    setRender(createNodes(text, cutOutPElement));
  }, [text, values]);

  const render =
    typeof toRender === "string"
      ? toRender
      : toRender.map((val) => {
          if (typeof val === "string") {
            return val;
          }

          const res = values(val);

          return React.isValidElement(res)
            ? React.cloneElement(res, { key: val.index })
            : res;
        });

  return <>{wrapToSpan ? <span>{render}</span> : render}</>;
};

export default HtmlParser;
